import axios, { type AxiosInstance, type AxiosResponse } from 'axios';

// Get API URL from environment variables
const getAPIBaseURL = (): string => {
  // Use REACT_APP_API_URL from .env file
  // For development: http://localhost:5000/api
  // For production: https://your-backend.azurecontainerapps.io/api
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  
  console.log('🔗 API Base URL:', apiUrl);
  console.log('🌍 Environment:', process.env.NODE_ENV);
  return apiUrl;
};

const API: AxiosInstance = axios.create({
  baseURL: getAPIBaseURL(),
  timeout: 30000, // Increased timeout to 30 seconds for better reliability
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: false, // Set to true if using cookies
});

const ServerAPI: AxiosInstance = axios.create({
  baseURL: getAPIBaseURL().replace('/api', ''), // Remove /api suffix
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: false,
});

API.interceptors.request.use(
  config => {
    const token = localStorage.getItem('token'); // Changed from 'authToken' to 'token' to match AuthContext
    if (token) {
      config.headers = config.headers ?? {};
      config.headers['Authorization'] = `Bearer ${token}`; // Changed to Bearer token format
    }
    
    // Add request timing for debugging (using any to bypass TypeScript restriction)
    (config as any).metadata = { startTime: new Date() };
    
    return config;
  },
  error => Promise.reject(error)
);

API.interceptors.response.use(
  response => {
    // Log response time for debugging slow requests
    const metadata = (response.config as any).metadata;
    if (metadata?.startTime) {
      const endTime = new Date();
      const duration = endTime.getTime() - metadata.startTime.getTime();
      if (duration > 3000) {
        console.warn(`Slow API request: ${response.config.url} took ${duration}ms`);
      }
    }
    return response;
  },
  error => {
    console.error('API Error:', error.response ?? error);

    if (error.response && (error.response.status === 401 || error.response.status === 403)) {
      // Clear auth data and redirect to login
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }

    return Promise.reject(error);
  }
);

ServerAPI.interceptors.response.use(
  response => response,
  error => {
    console.error('Server API Error:', error.response ?? error);
    return Promise.reject(error);
  }
);

type Params = Record<string, unknown> | undefined;

type GetOptions<T> = {
  fallbackValue?: T;
  suppressErrorLog?: boolean;
};

const apiService = {
  get: async <T = unknown>(url: string, params?: Params, options?: GetOptions<T>): Promise<T> => {
    try {
      const response: AxiosResponse<T> = await API.get<T>(url, { params });
      if (response.data === undefined || response.data === null) {
        console.warn(`No data in response from ${url}`);
        if (options?.fallbackValue !== undefined) {
          return options.fallbackValue;
        }
        return ([] as unknown) as T;
      }
      return response.data;
    } catch (error) {
      if (!options?.suppressErrorLog) {
        console.error(`Error fetching from ${url}:`, error);
      }
      if (options?.fallbackValue !== undefined) {
        return options.fallbackValue;
      }
      return ([] as unknown) as T;
    }
  },

  post: async <T = unknown, D = unknown>(url: string, data?: D): Promise<T> => {
    const response: AxiosResponse<T> = await API.post<T>(url, data);
    return response.data;
  },

  put: async <T = unknown, D = unknown>(url: string, data?: D): Promise<T> => {
    const response: AxiosResponse<T> = await API.put<T>(url, data);
    return response.data;
  },

  patch: async <T = unknown, D = unknown>(url: string, data?: D): Promise<T> => {
    const response: AxiosResponse<T> = await API.patch<T>(url, data);
    return response.data;
  },

  delete: async <T = unknown>(url: string): Promise<T> => {
    const response: AxiosResponse<T> = await API.delete<T>(url);
    return response.data;
  },
};

const serverService = {
  get: async <T = unknown>(url: string, params?: Params): Promise<T> => {
    const response: AxiosResponse<T> = await ServerAPI.get<T>(url, { params });
    return response.data;
  },
};

const getImageUrl = (imagePath?: string | null): string => {
  if (!imagePath) return '';

  if (/^https?:\/\//i.test(imagePath)) {
    return imagePath;
  }

  const AZURE_STORAGE_BASE_URL = 'https://tafsirart1759840013222.blob.core.windows.net/images';

  if (imagePath.startsWith('/Images/')) {
    const filename = imagePath.replace('/Images/', '');
    return `${AZURE_STORAGE_BASE_URL}/${encodeURIComponent(filename)}`;
  }

  if (!imagePath.startsWith('/')) {
    return `${AZURE_STORAGE_BASE_URL}/${encodeURIComponent(imagePath)}`;
  }

  const cleanPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
  const configuredBase = process.env.REACT_APP_IMAGE_BASE_URL;
  if (configuredBase) {
    return `${configuredBase.replace(/\/$/, '')}${cleanPath}`;
  }

  if (typeof window !== 'undefined' && window.location) {
    return `${window.location.origin}${cleanPath}`;
  }

  const publicUrl = process.env.PUBLIC_URL || '';
  return `${publicUrl}${cleanPath}`.replace(/\/\/+/, '/');
};

export { API, ServerAPI, apiService, serverService, getImageUrl };
export default API;
